const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v2';
const isDev = process.env.NODE_ENV === 'development';

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
    message?: string;
}

// Helper to get auth token from localStorage
function getAuthToken(): string | null {
    return localStorage.getItem('imperialismmaps_token');
}

/** Share one in-flight promise per GET URL (React 18 Strict Mode / remount). */
const inflightGets = new Map<string, Promise<ApiResponse<unknown>>>();

async function fetchApi<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<ApiResponse<T>> {
    try {
        const url = `${API_BASE_URL}${endpoint}`;
        if (isDev) console.log('API Request:', url);
        
        // Get auth token and add to headers if available
        const token = getAuthToken();
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(options.headers as Record<string, string>),
        };
        
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(url, {
            ...options,
            headers,
        });
        
        if (isDev) console.log('API Response:', response.status, response.headers.get('content-type'));

        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            const text = await response.text();
            return {
                success: false,
                error: `Expected JSON but received ${contentType || 'unknown'}. Is the backend running?`,
                message: text.substring(0, 200), // First 200 chars of response
            };
        }

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                error: data.error || 'Request failed',
                message: data.message,
                // Preserve the parsed body so callers can read structured error
                // payloads (e.g. a 402 payment-required checkout descriptor).
                data: data,
            };
        }

        return {
            success: true,
            data: data,
        };
    } catch (error) {
        if (error instanceof TypeError && error.message.includes('fetch')) {
            return {
                success: false,
                error: 'Cannot connect to backend. Please check if the API server is running.',
            };
        }
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Network error',
        };
    }
}

function getDeduped<T>(endpoint: string): Promise<ApiResponse<T>> {
    const key = `${API_BASE_URL}${endpoint}`;
    const existing = inflightGets.get(key);
    if (existing) return existing as Promise<ApiResponse<T>>;
    const pending = fetchApi<T>(endpoint, { method: 'GET' }).finally(() => {
        if (inflightGets.get(key) === pending) inflightGets.delete(key);
    });
    inflightGets.set(key, pending as Promise<ApiResponse<unknown>>);
    return pending;
}

export const api = {
    get: <T>(endpoint: string) => getDeduped<T>(endpoint),
    post: <T>(endpoint: string, body?: unknown) =>
        fetchApi<T>(endpoint, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    put: <T>(endpoint: string, body?: unknown) =>
        fetchApi<T>(endpoint, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
    patch: <T>(endpoint: string, body?: unknown) =>
        fetchApi<T>(endpoint, {
            method: 'PATCH',
            body: JSON.stringify(body),
        }),
    delete: <T>(endpoint: string) => fetchApi<T>(endpoint, { method: 'DELETE' }),
};

